import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { AdminPage, AdminState, Field, Notice, SkeletonList, isForbidden } from '@/admin/AdminShell'
import { RecordPicker } from '@/card/RecordPicker'
import { StateView } from '@/card/record'
import { ApiError } from '@/lib/api'
import {
  assignConversation,
  dismissConversation,
  fetchMessengerQueue,
  ticketFromConversation,
  type MessengerAssignTarget,
  type MessengerQueueItem,
} from './api'

/**
 * Очередь разбора диалогов мессенджеров (тикет #125).
 *
 * Сюда попадает чат, собеседника которого система не узнала: Telegram не называет номер, пока
 * человек сам не поделится контактом, а номер WhatsApp может не найтись в карточках или найтись
 * у нескольких. Сам диалог никуда не кладётся: подсказка по @username из карточки контакта
 * показана словами, но решает человек — ник мог перейти к другому.
 *
 * Право — `messenger.inbox`: `read` видит очередь, `update` привязывает, отклоняет и заводит
 * обращение. Привязать можно только к карточке, которую вызывающий видит, — чужая отвечает
 * отказом, как отсутствующая.
 */
const TARGETS: { type: MessengerAssignTarget; entity: string; search: string; extra?: string }[] = [
  { type: 'contact', entity: 'sales.contact', search: 'last_name', extra: 'first_name' },
  { type: 'counterparty', entity: 'sales.counterparty', search: 'name', extra: 'inn' },
]

export function MessengerInboxPage() {
  const { t, user } = useApp()
  const queue = useQuery({ queryKey: ['messenger-inbox'], queryFn: ({ signal }) => fetchMessengerQueue(signal) })
  const mayDecide = can(user, 'messenger.inbox', 'update')
  const items = queue.data ?? []

  return (
    <AdminPage
      titleKey="messenger.inbox.title"
      leadKey="messenger.inbox.lead"
      sectionKey="nav.section.work"
      count={queue.data ? items.length : undefined}
      readOnly={!mayDecide}
      roKey="messenger.inbox.ro"
      forbidden={isForbidden(queue.error)}
    >
      <AdminState
        isLoading={queue.isLoading}
        error={queue.error}
        onRetry={() => void queue.refetch()}
        forbiddenTitleKey="messenger.inbox.forbidden"
        errorTitleKey="messenger.inbox.error"
        skeleton={<SkeletonList rows={4} />}
      >
        {items.length === 0 ? (
          <StateView kind="empty" boxed compact title={t('messenger.inbox.empty')} description={t('messenger.inbox.empty.text')} />
        ) : (
          <ul className="stack" style={{ gap: 12, listStyle: 'none', padding: 0 }} aria-label={t('messenger.inbox.title')}>
            {items.map((item) => <QueueRow key={item.id} item={item} mayDecide={mayDecide} />)}
          </ul>
        )}
      </AdminState>
    </AdminPage>
  )
}

function QueueRow({ item, mayDecide }: { item: MessengerQueueItem; mayDecide: boolean }) {
  const { t, locale, user } = useApp()
  const queryClient = useQueryClient()
  const [target, setTarget] = useState<MessengerAssignTarget>('contact')
  const [picked, setPicked] = useState<{ id: string; label: string } | null>(
    item.suggestedContactId != null
      ? { id: item.suggestedContactId, label: item.suggestedContactName ?? item.suggestedContactId }
      : null,
  )
  const [notice, setNotice] = useState<string | null>(null)
  const done = () => {
    setNotice(null)
    void queryClient.invalidateQueries({ queryKey: ['messenger-inbox'] })
  }
  const fail = (error: unknown) => setNotice(error instanceof ApiError ? error.message : t('app.error'))
  const assign = useMutation({
    mutationFn: (record: { id: string }) => assignConversation(item.id, target, record.id),
    onSuccess: done,
    onError: fail,
  })
  const dismiss = useMutation({ mutationFn: () => dismissConversation(item.id), onSuccess: done, onError: fail })
  const mayOpenTicket = can(user, 'support.ticket', 'create')
  const toTicket = useMutation({
    mutationFn: (record: { id: string }) => ticketFromConversation(item.id, record.id),
    onSuccess: () => {
      done()
      void queryClient.invalidateQueries({ queryKey: ['list', 'support.ticket'] })
    },
    onError: fail,
  })
  const spec = TARGETS.find((candidate) => candidate.type === target) ?? TARGETS[0]!
  const pickerId = `messenger-${item.id}`
  const who = [item.username ? `@${item.username}` : null, item.phone].filter(Boolean).join(' · ')

  return (
    <li className="panel stack" style={{ gap: 8 }} aria-label={item.title}>
      <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
        <span className="badge">{t(`messenger.channel.${item.channel}`)}</span>
        <span className="strong">{item.title}</span>
        {who && <span className="muted t-sm">{who}</span>}
        <span className="spacer" />
        {item.lastMessageAt != null && <span className="muted t-sm">{formatMoment(item.lastMessageAt, locale)}</span>}
      </div>
      <ul className="t-sm" aria-label={t('messenger.inbox.messages')} style={{ margin: 0 }}>
        {(item.messages ?? []).map((message) => (
          <li key={message.id}>
            <span className="muted">{t(`messenger.direction.${message.direction}`)}: </span>
            {message.body}
          </li>
        ))}
      </ul>
      {item.reason && <span className="t-sm muted" role="note">{item.reason}</span>}
      {item.suggestedContactId != null && (
        <span className="t-sm">{t('messenger.inbox.suggestion', { name: item.suggestedContactName ?? '' })}</span>
      )}
      {mayDecide && (
        <div className="row" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <Field label={t('messenger.inbox.attachTo')} htmlFor={`${pickerId}-type`}>
            <select id={`${pickerId}-type`} className="input" value={target} onChange={(event) => {
              setTarget(event.target.value as MessengerAssignTarget)
              setPicked(null)
            }}>
              {TARGETS.map((candidate) => (
                <option key={candidate.type} value={candidate.type}>{t(`inbox.target.${candidate.type}`)}</option>
              ))}
            </select>
          </Field>
          <Field label={t(`inbox.target.${target}`)} htmlFor={pickerId}>
            <RecordPicker id={pickerId} entity={spec.entity} searchColumn={spec.search} extraColumn={spec.extra}
                          value={picked} onChange={setPicked} />
          </Field>
          <button type="button" className="btn btn-primary" disabled={picked == null || assign.isPending}
                  onClick={() => picked && assign.mutate(picked)}>
            {t('messenger.inbox.attach')}
          </button>
          {/* Обращение (#123) заводится на клиента, поэтому только при выборе контрагента. */}
          {target === 'counterparty' && mayOpenTicket && (
            <button type="button" className="btn btn-secondary" disabled={picked == null || toTicket.isPending}
                    onClick={() => picked && toTicket.mutate(picked)}>
              {t('inbox.toTicket')}
            </button>
          )}
          <button type="button" className="btn btn-ghost" disabled={dismiss.isPending} onClick={() => dismiss.mutate()}>
            {t('inbox.dismiss')}
          </button>
        </div>
      )}
      <Notice text={notice} />
    </li>
  )
}

function formatMoment(value: string, locale: string): string {
  const moment = new Date(value)
  if (Number.isNaN(moment.getTime())) return value
  return moment.toLocaleString(locale === 'en' ? 'en-GB' : 'ru-RU')
}
